import isISO8601 from 'validator/lib/isISO8601';
import { z } from 'zod';

import {
  AuditlogEventType,
  Email,
  EventId,
  LoginId,
  LoginRevisionId,
  OrganizationId,
  ProfileId,
  ShareSecretId,
  UUID,
  VaultId,
} from 'backend-client-web';

import { makeProtobufEnumSchema } from '../enumSchemas';
import { LoginDifference } from '../loginDifference/LoginDifference';
import { ISODateString } from '../types';
import { WithUnknownValuePlaceholder } from '../unknownValuePlaceholder';

interface AuditlogEventTargetProfile {
  id: ProfileId;
  email: Email;
}

const AuditlogEventTargetProfile = z.object({
  id: z.uuid(),
  email: z.string(),
});

interface AuditlogEvent {
  type: WithUnknownValuePlaceholder<AuditlogEventType>;

  vaultId?: VaultId;
  loginId?: LoginId;
  customFieldId?: UUID;
  loginRevisionId?: LoginRevisionId;
  loginEditTime?: ISODateString;
  loginDifferences?: LoginDifference[];
  targetProfile?: AuditlogEventTargetProfile;
  shareSecretId?: ShareSecretId;
  sourceVaultId?: VaultId;
}

const AuditlogEvent = z.object({
  type: makeProtobufEnumSchema(AuditlogEventType, AuditlogEventType.UNKNOWN),

  vaultId: z.uuid().optional(),
  loginId: z.uuid().optional(),
  customFieldId: z.uuid().optional(),
  loginRevisionId: z.uuid().optional(),
  loginEditTime: z
    .string()
    .refine((str) => isISO8601(str))
    .optional(),
  loginDifferences: z.array(LoginDifference).optional(),
  targetProfile: AuditlogEventTargetProfile.optional(),
  shareSecretId: z.string().optional(),
  sourceVaultId: z.uuid().optional(),
});

interface AuditlogEventEnvelope {
  id: EventId;
  profileId: ProfileId;
  time: ISODateString;
  clientIp?: string;

  event: AuditlogEvent;
}

// The event envelope wraps an AuditlogEvent and adds server-defined metadata.
const AuditlogEventEnvelope = z.object({
  id: z.uuid(),
  profileId: z.uuid(),
  time: z.string().refine((str) => isISO8601(str)),
  clientIp: z.string().optional(),

  event: AuditlogEvent,
});

interface QueuedAuditlogEvent {
  organizationId: OrganizationId;
  clientTime: Date;
  lastKnownServerTime: Date;

  event: AuditlogEvent;
}

const QueuedAuditlogEvent = z.object({
  organizationId: z.uuid(),
  clientTime: z.date(),
  lastKnownServerTime: z.date(),

  event: AuditlogEvent,
});

export { AuditlogEventEnvelope, AuditlogEvent, QueuedAuditlogEvent, AuditlogEventTargetProfile };
